import { useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { useDisclosure } from '@chakra-ui/react'

import { DateValueI } from '@/components/ui'
import { useSubmittedToursQuery } from '@/graphql/generated/hooks'
import {
  GuardTourOrderField,
  OrderDirection,
} from '@/graphql/generated/schemas'
import { getCreatedAtGteTimestamp } from '@/redux/incidents/utils'
import { selectMe } from '@/redux/me/meSlice'
import {
  selectSideNavOpen,
  selectSubmittedToursFacilityIdsFilter,
  selectSubmittedToursGuardIdsFilter,
  selectSubmittedToursPageSize,
  selectSubmittedToursStatusFilter,
  selectSubmittedToursSubmittedAtRangeFilter,
} from '@/redux/ui/uiSlice'
import { FilterOptionI, isCustomDateRange } from '@/utils/filterDrawers'

export const useSubmittedTours = () => {
  const me = useSelector(selectMe)
  const dispatch = useDispatch()

  const isSideNavOpen = useSelector(selectSideNavOpen)
  const pageSize = useSelector(selectSubmittedToursPageSize)

  const [nameFilter, setNameFilter] = useState(null)
  const [debouncedNameFilter] = useDebounce(nameFilter, 1000)
  const isGuardToursV2 = useFeatureFlag(FeatureFlag.guardToursV2)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const facilityIdsFilterValue = useSelector(
    selectSubmittedToursFacilityIdsFilter
  )
  const guardIdsFilterValue = useSelector(selectSubmittedToursGuardIdsFilter)
  const submittedAtFilterValue = useSelector(
    selectSubmittedToursSubmittedAtRangeFilter
  )
  const statusFilterValue = useSelector(selectSubmittedToursStatusFilter)

  const submittedAtOption = submittedAtFilterValue as FilterOptionI<DateValueI>

  const submittedAtFilter = useMemo(() => {
    if (isCustomDateRange(submittedAtOption)) {
      return {
        submittedAtGte: submittedAtOption?.value?.gte,
        submittedAtLte: submittedAtOption?.value?.lte,
      }
    }

    if (submittedAtFilterValue?.value) {
      return {
        submittedAtGte: getCreatedAtGteTimestamp(
          Date.now(),
          submittedAtFilterValue.value as number
        ),
      }
    }

    return {}
  }, [
    submittedAtFilterValue?.value,
    submittedAtOption?.value?.gte,
    submittedAtOption?.value?.lte,
  ])

  const filters = {
    facilityIdIn: facilityIdsFilterValue?.length
      ? facilityIdsFilterValue?.map((f) => f.value)
      : undefined,
    guardIdIn: guardIdsFilterValue?.length
      ? guardIdsFilterValue?.map((g) => g.value)
      : undefined,

    statusIn: statusFilterValue?.length
      ? statusFilterValue.map((s) => s.value)
      : undefined,

    tourPlanNameLike: debouncedNameFilter,
  }

  const {
    data,
    loading: isSubmittedToursLoading,
    refetch,
  } = useSubmittedToursQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      filter: JSON.parse(
        JSON.stringify({
          ...filters,
          ...submittedAtFilter,
        })
      ),
      pageIndex: 0,
      pageSize: pageSize,
      orderBy: [
        {
          direction: OrderDirection.Desc,
          field: GuardTourOrderField.UpdatedAt,
        },
      ],
    },
  })

  const toursCount = data?.guardTours.totalCount
  const toursList = data?.guardTours.edges ?? []

  return {
    me,
    dispatch,
    isSideNavOpen,
    isGuardToursV2,
    isSubmittedTourModalOpen: isOpen,
    onSubmittedTourModalOpen: onOpen,
    onSubmittedTourModalClose: onClose,
    submittedToursData: data,
    isSubmittedToursLoading,
    refetch,
    nameFilter,
    setNameFilter,
    toursCount,
    toursList,
  }
}
